import { CardPicasso } from "@liberetech/design-system";
import Hero from "@liberetech/design-system/Hero";
import NavLink from "@liberetech/design-system/NavLink";
import classNames from "classnames";

import { Page } from "components/Context/PageContext";
import { Seo } from "components/Seo/Seo";
import fetch from "lib/client/cms";
import serverSideTranslations from "lib/i18n/_pagesRouterCompat/serverSideTranslations";
import { useTranslation } from "lib/i18n/client";
import { useTranslation as useTranslationServer } from "lib/i18n/server";
import { TrackingContextPage } from "lib/tracking";
import withDirectChannelCommonProps from "lib/withDirectChannelCommonProps";
import { DirectChannelCommonProps } from "types";

import styles from "./travel-guide.module.css";

const DestinationTravelGuide: React.FC<
  DestinationTravelGuideProps & DirectChannelCommonProps
> = ({ destination }) => {
  const { t } = useTranslation("destinationTravelGuide");

  const title = t("title", { destination: destination.title });
  const { posts } = destination;
  return (
    <>
      <Page title={title} backHref="/" />
      <Seo title={title} />
      <Hero
        breadcrumbs={[
          { path: "/", title: t("common:breadcrumbs.home") },
          {
            path: destination.uri,
            title: t("common:breadcrumbs.destination", {
              city: destination.title,
            }),
          },
        ]}
        title={title}
      />
      <div className={classNames(styles.posts, "grid")}>
        {posts.edges.map(({ node: post }, i) => (
          <NavLink href={post.uri} key={i} className={styles.postPreview}>
            <CardPicasso
              image={
                post.featuredImage && (
                  <img
                    alt=""
                    srcSet={post.featuredImage.node.srcSet}
                    loading="lazy"
                    sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                  />
                )
              }
              title={post.title}
            />
          </NavLink>
        ))}
      </div>
    </>
  );
};

export const getServerSideProps =
  withDirectChannelCommonProps<DestinationTravelGuideProps>(
    async ({ locale, params, req }) => {
      if (!locale || !params || !params.slug || Array.isArray(params.slug)) {
        return {
          notFound: true,
        };
      }
      const { destination } = (await fetch(
        `
      query {
        destination(id: "/${locale}/${params.slug}", idType: URI) {
          title,
          uri
          translations {
            language {
              slug
            }
            uri
          }
          posts(first: 100) {
            edges {
              node {
                title
                uri
                featuredImage {
                  node {
                    srcSet
                  }
                }
              }
            }
          }
        }
      }
    `,
        req.context.brand.cmsUrl,
      )) as { destination: DestinationResponse };

      if (!destination || destination.title === null) {
        return {
          notFound: true,
        };
      }

      const translations = destination.translations.filter(
        (translation) => translation && translation.language,
      );

      const ts = await Promise.all([
        useTranslationServer(locale, "common"),
        useTranslationServer(locale, "destinationTravelGuide"),
        ...translations.map((translation) =>
          useTranslationServer(translation.language.slug, "common"),
        ),
      ]);

      return {
        props: {
          ...(await serverSideTranslations(
            "/destinations/[slug]/travel-guide",
            locale,
          )),
          destination,
          trackingContext: {
            page_type: "destination_travel_guide",
          },
          languageAlternates: [
            {
              hrefLang: locale,
              href: `${destination.uri}/${ts[0]("routes.travelGuide")}`,
            },
            ...translations.map((translation, i) => ({
              hrefLang: translation.language.slug,
              href: `${translation.uri}/${ts[i + 2]("routes.travelGuide")}`,
            })),
          ],
          breadcrumbs: [
            { name: ts[0]("common:breadcrumbs.home"), item: `/${locale}` },
            {
              name: ts[0]("common:breadcrumbs.destination", {
                city: destination.title,
              }),
              item: destination.uri,
            },
            {
              name: ts[1]("title", { destination: destination.title }),
              item: `${destination.uri}/${ts[0]("common:routes.travelGuide")}`,
            },
          ],
        },
      };
    },
  );

type DestinationTravelGuideProps = {
  destination: DestinationResponse;
} & { trackingContext: TrackingContextPage };

type DestinationResponse = {
  title: string;
  uri: string;
  translations: Array<{
    language: {
      slug: string;
    };
    uri: string;
  }>;
  posts: {
    edges: Array<{
      node: {
        title: string;
        uri: string;
        featuredImage: {
          node: {
            srcSet: string;
          };
        };
      };
    }>;
  };
};

export default DestinationTravelGuide;
